// ─── utils/apiClient.js ────────────────────────────────────────────────────
// Single authenticated fetch wrapper for the whole app.
//
// Every request goes through apiFetch(), which:
//   - attaches `Authorization: Bearer <access token>`
//   - on 401, tries ONE refresh via /api/auth/refresh, then retries the request
//   - if refresh fails, clears tokens and dispatches an 'aegis:logout' event
//     so AuthGate can bounce the user back to the login screen
//
// Token storage: sessionStorage. This is readable by JS (so vulnerable to XSS),
// but simplest to ship. For a hardened commercial build, move to httpOnly
// cookies + a backend CORS/cookie change — see notes at the bottom.
// ───────────────────────────────────────────────────────────────────────────

const API_BASE = '/api';

const ACCESS_KEY  = 'aegis_access_token';
const REFRESH_KEY = 'aegis_refresh_token';
const USER_KEY    = 'aegis_user';

// ── token storage helpers ──────────────────────────────────────────────────
export const tokenStore = {
  getAccess()  { return sessionStorage.getItem(ACCESS_KEY); },
  getRefresh() { return sessionStorage.getItem(REFRESH_KEY); },
  getUser() {
    try { return JSON.parse(sessionStorage.getItem(USER_KEY) || 'null'); }
    catch { return null; }
  },
  set({ access_token, refresh_token, user }) {
    if (access_token)  sessionStorage.setItem(ACCESS_KEY, access_token);
    if (refresh_token) sessionStorage.setItem(REFRESH_KEY, refresh_token);
    if (user)          sessionStorage.setItem(USER_KEY, JSON.stringify(user));
  },
  clear() {
    sessionStorage.removeItem(ACCESS_KEY);
    sessionStorage.removeItem(REFRESH_KEY);
    sessionStorage.removeItem(USER_KEY);
  },
};

function emitLogout() {
  window.dispatchEvent(new CustomEvent('aegis:logout'));
}

// ── single-flight refresh so concurrent 401s don't stampede ─────────────────
let refreshInFlight = null;

async function refreshAccessToken() {
  if (refreshInFlight) return refreshInFlight;

  const refresh_token = tokenStore.getRefresh();
  if (!refresh_token) return null;

  refreshInFlight = (async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token }),
      });
      if (!res.ok) return null;
      const data = await res.json();
      tokenStore.set({
        access_token: data.access_token,
        refresh_token: data.refresh_token,
      });
      return data.access_token;
    } catch {
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

// ── main wrapper ────────────────────────────────────────────────────────────
// Same signature as fetch, but URL may be an /api-relative path.
export async function apiFetch(path, options = {}) {
  const url = path.startsWith('http') ? path : `${API_BASE}${path.replace(/^\/api/, '')}`;

  const doRequest = (token) => {
    const headers = { ...(options.headers || {}) };
    // Only set JSON content-type when we're sending a plain body (not FormData).
    if (options.body && !(options.body instanceof FormData) && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return fetch(url, { ...options, headers });
  };

  let res = await doRequest(tokenStore.getAccess());

  if (res.status === 401) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      res = await doRequest(newToken);       // retry once with fresh token
    }
    if (res.status === 401) {
      tokenStore.clear();
      emitLogout();
    }
  }

  return res;
}

// Convenience: apiFetch + JSON parse, throws on non-OK.
export async function apiJson(path, options = {}) {
  const res = await apiFetch(path, options);
  if (!res.ok) {
    const err = new Error(`HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

// ── auth actions ────────────────────────────────────────────────────────────
export async function login(username, password) {
  const res = await fetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  if (!res.ok) {
    let detail = 'Invalid username or password';
    try { detail = (await res.json()).detail || detail; } catch {}
    const err = new Error(detail);
    err.status = res.status;
    throw err;
  }
  const data = await res.json();
  const user = {
    username,
    role: data.role,
    permissions: data.permissions || [],
    must_change_password: !!data.must_change_password,
  };
  tokenStore.set({
    access_token: data.access_token,
    refresh_token: data.refresh_token,
    user,
  });
  return user;
}

export async function logout() {
  const refresh_token = tokenStore.getRefresh();
  if (refresh_token) {
    try {
      await fetch(`${API_BASE}/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token }),
      });
    } catch { /* best-effort */ }
  }
  tokenStore.clear();
}

export async function changePassword(current_password, new_password) {
  return apiJson('/auth/change-password', {
    method: 'POST',
    body: JSON.stringify({ current_password, new_password }),
  });
}

// ── NOTE on hardening ───────────────────────────────────────────────────────
// sessionStorage tokens are XSS-readable. Before commercial launch, prefer
// httpOnly, Secure, SameSite cookies set by the backend, which JS cannot read.
// That needs: backend sets cookies on login/refresh; CORS allow_credentials
// (already on); frontend sends credentials: 'include' and drops the manual
// Authorization header. Deferred here for simplicity — flagged, not forgotten.
